@if (loading()) {
  <div class="loading-spinner--container">
    <app-loading-spinner></app-loading-spinner>
  </div>
} @else if (fetchCalibrationError()) {
  <app-error-page
    [primaryText]="fetchCalibrationError()?.message"
  ></app-error-page>
} @else {
  <div id="title--container">
    <h4>{{ calibrationTitle() }}</h4>
    @if (activeCalibrationIsDeleted()) {
      <app-status-indicator status="Deleted"></app-status-indicator>
    }
    <h5>{{ laboratory()?.laboratoryName }}</h5>
  </div>
  <hr />
  <div id="inner-page--container">
    <mat-accordion multi>
      <mat-expansion-panel [expanded]="true" hideToggle>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <div class="expansion-header">Calibration Details</div>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <div class="details-layout">
          @if (activeCalibration()) {
            <div id="data-cards">
              <app-reduced-small-data-card
                title="Name"
                [dataReading]="activeCalibration().name || '-'"
              >
              </app-reduced-small-data-card>
              <app-reduced-small-data-card
                title="Save Location"
                [dataReading]="
                  activeCalibration().saveLocationName?.toString() || '-'
                "
              >
              </app-reduced-small-data-card>
              @if (activeCalibration().volume != null) {
                <app-reduced-small-data-card
                  title="Volume (mL)"
                  [dataReading]="
                    activeCalibration().volume?.toString() ?? 'N/A'
                  "
                >
                </app-reduced-small-data-card>
              }
              <app-reduced-small-data-card
                title="Status"
                [status]="
                  activeCalibration().isFinal ? 'Finalised' : 'In Progress'
                "
              >
              </app-reduced-small-data-card>
              @if (calibrationType() === "oxygen") {
                <app-reduced-small-data-card
                  title="Created By"
                  [dataReading]="activeCalibration().createdBy || '-'"
                >
                </app-reduced-small-data-card>
                <app-reduced-small-data-card
                  title="Notification Email"
                  [dataReading]="activeCalibration().notificationEmail || '-'"
                >
                </app-reduced-small-data-card>
              }
              <app-reduced-small-data-card
                title="Created At"
                [dataReading]="
                  (activeCalibration().createdAt | date: 'HH:mm, dd/MM/yy') ||
                  '-'
                "
              >
              </app-reduced-small-data-card>
            </div>
          }
          @if (activeCalibrationIsFinal() && !activeCalibrationIsDeleted()) {
            <div class="action-button--container">
              <app-mat-icon-button
                buttonStyle="primary"
                buttonId="edit-calibration-btn"
                ariaLabel="Edit calibration"
                iconName="edit"
                (buttonClicked)="editCalibration()"
                [isDisabled]="
                  !(currentUser()?.role | roleCheck: editAccessRoles)
                "
              ></app-mat-icon-button>
              <app-mat-icon-button
                buttonStyle="negative"
                buttonId="delete-calibration-btn"
                ariaLabel="Delete calibration"
                iconName="delete"
                (buttonClicked)="handleDiscardAndDelete('delete')"
                [isDisabled]="
                  !(currentUser()?.role | roleCheck: deletePermittedRoles())
                "
              ></app-mat-icon-button>
            </div>
          }
          @if (!activeCalibrationIsFinal() && !activeCalibrationIsDeleted()) {
            <div class="action-button--container">
              <app-mat-icon-button
                buttonStyle="negative"
                buttonId="discard-calibration-btn"
                ariaLabel="Discard calibration"
                iconName="delete"
                (buttonClicked)="handleDiscardAndDelete('discard')"
                [isDisabled]="
                  !(currentUser()?.role | roleCheck: editAccessRoles)
                "
              ></app-mat-icon-button>
            </div>
          }
        </div>
      </mat-expansion-panel>
      @if (
        calibrationType() === "optical-density" && !odCalibration()?.isFinal
      ) {
        <mat-expansion-panel [expanded]="true" hideToggle>
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="expansion-header">Reactor Values</div>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div class="table-toolbar">
            <div class="od-table-legend" aria-hidden="true">
              <span class="legend-item">
                <span class="legend-box" aria-hidden="true"></span>
                <span>OD reading</span>
              </span>
              <span class="legend-item">
                <span class="legend-pill" aria-hidden="true"></span>
                <span>Voltage</span>
              </span>
            </div>
            <div class="button--container no-padding">
              <app-icon-button
                [label]="voltageLabel()"
                buttonId="poll-voltage-btn"
                (buttonClicked)="getVoltageValues()"
                [isProcessing]="isVoltagePolling()"
                iconName="autorenew"
                buttonShape="square"
                buttonStyle="outlined"
                [isDisabled]="
                  finalisedMessageSentToDevice() ||
                  isVoltagePolling() ||
                  isCalibrationPolling()
                "
              ></app-icon-button>
              <app-button
                label="Stop Polling"
                buttonId="stop-voltage-polling-btn"
                [isDisabled]="!isVoltagePolling()"
                (buttonClicked)="stopVoltagePolling()"
              >
              </app-button>
            </div>
          </div>
          <p id="od-table-description" class="sr-only">
            Legend: the outlined box indicates the OD reading input. The grey
            pill indicates the voltage value.
          </p>
          <div class="od-table-wrapper">
            <table
              mat-table
              [dataSource]="odFlaskFields"
              class="od-table"
              aria-label="Sample OD readings"
              aria-describedby="od-table-description"
            >
              <!-- Reactor column -->
              <ng-container matColumnDef="reactor">
                <th mat-header-cell *matHeaderCellDef class="reactor-head">
                  Flasks / Data point
                </th>
                <td
                  mat-cell
                  *matCellDef="let flask"
                  role="rowheader"
                  class="reactor-cell"
                >
                  <div class="reactor-cell-inner">
                    <span
                      class="reactor-dot"
                      [style.background]="flask.color"
                      aria-hidden="true"
                    ></span>
                    <span class="reactor-id subtitle2">{{
                      flask.flaskId
                    }}</span>
                  </div>
                </td>
              </ng-container>
              <!-- Data columns 1–6 -->
              @for (n of [1, 2, 3, 4, 5, 6]; track n) {
                <ng-container [matColumnDef]="'col-' + n">
                  <th mat-header-cell *matHeaderCellDef>
                    <span class="col-header-inner">
                      {{ n }}
                      <button
                        type="button"
                        class="copy-col-btn"
                        [attr.aria-label]="
                          'Copy reactor A value to all reactors for column ' + n
                        "
                        title="Copy reactor A value to B, C, D"
                        [disabled]="!canEditOdReadings()"
                        (click)="copyColumnVoltages(n - 1)"
                      >
                        <mat-icon aria-hidden="true">content_copy</mat-icon>
                      </button>
                    </span>
                  </th>
                  <td
                    mat-cell
                    *matCellDef="let flask; let rowIdx = index"
                    class="data-cell"
                  >
                    <div class="od-cell-content">
                      <app-table-input
                        [control]="odControl(flask.fields[n - 1].key)"
                        [label]="
                          'Channel ' +
                          flask.flaskId +
                          ', OD reading for column ' +
                          n
                        "
                        [rowIndex]="rowIdx"
                        [colIndex]="n - 1"
                        [step]="odStep"
                        [min]="minODValue"
                        [max]="maxODValue"
                        [readOnly]="!canEditOdReadings()"
                        (navigate)="navigateOdCell($event)"
                      ></app-table-input>
                      <span
                        class="voltage-pill body2"
                        [class.is-pending]="
                          getVoltageValue(flask.voltagePrefix, n) == null
                        "
                        [attr.aria-label]="
                          'Channel ' +
                          flask.flaskId +
                          ', voltage value for column ' +
                          n
                        "
                      >
                        {{ getVoltageValue(flask.voltagePrefix, n) ?? "–" }}
                      </span>
                    </div>
                  </td>
                </ng-container>
              }

              <tr mat-header-row *matHeaderRowDef="odDisplayedColumns"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: odDisplayedColumns"
              ></tr>
            </table>
          </div>
          @if (!finalisedMessageSentToDevice()) {
            <div class="finalise--container">
              <div class="od-cal-info body2">
                <mat-icon aria-hidden="true">info</mat-icon>
                <span>
                  Enter an OD reading between {{ minODValue }} and
                  {{ maxODValue }}, in increments of {{ odStep }}, for every
                  reactor voltage received. A reading per voltage must be
                  entered before the calibration can be finalised.
                </span>
              </div>
              <div class="finalise--actions">
                <app-button
                  label="Save Readings"
                  buttonId="save-readings-btn"
                  [isDisabled]="
                    odCalibration()?.isFinal ||
                    !(currentUser()?.role | roleCheck: editAccessRoles) ||
                    saveReadingsIsProcessing() ||
                    finalisedMessageSentToDevice() ||
                    !hasVoltageReadings() ||
                    odCalibrationForm.invalid
                  "
                  (buttonClicked)="saveReadings()"
                  [isProcessing]="saveReadingsIsProcessing()"
                  buttonStyle="standard"
                ></app-button>
                <app-button
                  label="Finalise Calibration"
                  buttonId="finalise-calibration-btn"
                  [isDisabled]="
                    odCalibration()?.isFinal ||
                    !(currentUser()?.role | roleCheck: editAccessRoles) ||
                    finalisationIsProcessing() ||
                    finalisedMessageSentToDevice() ||
                    !hasVoltageReadings() ||
                    odCalibrationForm.invalid
                  "
                  (buttonClicked)="finaliseCalibration()"
                  [isProcessing]="finalisationIsProcessing()"
                  buttonStyle="positive"
                ></app-button>
              </div>
            </div>
          } @else {
            <div class="finalised-message positive body1">
              <mat-icon aria-hidden="true" class="positive">check</mat-icon>
              Finalise message has been sent to device
            </div>
          }
        </mat-expansion-panel>
      }
      @if (activeCalibrationIsFinal()) {
        <mat-expansion-panel [expanded]="true" hideToggle>
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="expansion-header">Results</div>
            </mat-panel-title>
          </mat-expansion-panel-header>
          @if (
            calibrationType() === "optical-density" && odCalibration()?.isFinal
          ) {
            <div class="flask-cards">
              <app-flask-card
                flaskLabel="A"
                [data]="{
                  kind: 'od',
                  offset: odCalibration()?.calOffset_A ?? null,
                  factor: odCalibration()?.calFactor_A ?? null,
                  power: odCalibration()?.calPower_A ?? null,
                  rSquared: odCalibration()?.r2_A ?? null,
                  temperature: odCalibration()?.resultsTemperatureA ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="B"
                [data]="{
                  kind: 'od',
                  offset: odCalibration()?.calOffset_B ?? null,
                  factor: odCalibration()?.calFactor_B ?? null,
                  power: odCalibration()?.calPower_B ?? null,
                  rSquared: odCalibration()?.r2_B ?? null,
                  temperature: odCalibration()?.resultsTemperatureB ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="C"
                [data]="{
                  kind: 'od',
                  offset: odCalibration()?.calOffset_C ?? null,
                  factor: odCalibration()?.calFactor_C ?? null,
                  power: odCalibration()?.calPower_C ?? null,
                  rSquared: odCalibration()?.r2_C ?? null,
                  temperature: odCalibration()?.resultsTemperatureC ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="D"
                [data]="{
                  kind: 'od',
                  offset: odCalibration()?.calOffset_D ?? null,
                  factor: odCalibration()?.calFactor_D ?? null,
                  power: odCalibration()?.calPower_D ?? null,
                  rSquared: odCalibration()?.r2_D ?? null,
                  temperature: odCalibration()?.resultsTemperatureD ?? null,
                }"
              ></app-flask-card>
            </div>
          }
          @if (calibrationType() === "oxygen" && oxygenCalibration()?.isFinal) {
            <div class="flask-cards">
              <app-flask-card
                flaskLabel="A"
                [data]="{
                  kind: 'oxygen',
                  dphi:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.dphi_0_A ?? null)
                      : (oxygenCalibration()?.dphi_100_A ?? null),
                  temperature:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.temperature_0_A ?? null)
                      : (oxygenCalibration()?.temperature_100_A ?? null),
                  pressure:
                    oxygenSaturation() === 1
                      ? (oxygenCalibration()?.pressure_100_A ?? null)
                      : null,
                  hasError:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.hasError_0_A ?? null)
                      : (oxygenCalibration()?.hasError_100_A ?? null),
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="B"
                [data]="{
                  kind: 'oxygen',
                  dphi:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.dphi_0_B ?? null)
                      : (oxygenCalibration()?.dphi_100_B ?? null),
                  temperature:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.temperature_0_B ?? null)
                      : (oxygenCalibration()?.temperature_100_B ?? null),
                  pressure:
                    oxygenSaturation() === 1
                      ? (oxygenCalibration()?.pressure_100_B ?? null)
                      : null,
                  hasError:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.hasError_0_B ?? null)
                      : (oxygenCalibration()?.hasError_100_B ?? null),
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="C"
                [data]="{
                  kind: 'oxygen',
                  dphi:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.dphi_0_C ?? null)
                      : (oxygenCalibration()?.dphi_100_C ?? null),
                  temperature:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.temperature_0_C ?? null)
                      : (oxygenCalibration()?.temperature_100_C ?? null),
                  pressure:
                    oxygenSaturation() === 1
                      ? (oxygenCalibration()?.pressure_100_C ?? null)
                      : null,
                  hasError:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.hasError_0_C ?? null)
                      : (oxygenCalibration()?.hasError_100_C ?? null),
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="D"
                [data]="{
                  kind: 'oxygen',
                  dphi:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.dphi_0_D ?? null)
                      : (oxygenCalibration()?.dphi_100_D ?? null),
                  temperature:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.temperature_0_D ?? null)
                      : (oxygenCalibration()?.temperature_100_D ?? null),
                  pressure:
                    oxygenSaturation() === 1
                      ? (oxygenCalibration()?.pressure_100_D ?? null)
                      : null,
                  hasError:
                    oxygenSaturation() === 0
                      ? (oxygenCalibration()?.hasError_0_D ?? null)
                      : (oxygenCalibration()?.hasError_100_D ?? null),
                }"
              ></app-flask-card>
            </div>
          }
          @if (calibrationType() === "ph" && phCalibration()?.isFinal) {
            <div class="flask-cards">
              <app-flask-card
                flaskLabel="A"
                [data]="{
                  kind: 'ph',
                  slope: phCalibration()?.slope_A ?? null,
                  mVOffset: phCalibration()?.offset_A ?? null,
                  phCalFactor: phCalibration()?.calFactor_A ?? null,
                  phCalOffset: phCalibration()?.calOffset_A ?? null,
                  rSquared: phCalibration()?.r2_A ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="B"
                [data]="{
                  kind: 'ph',
                  slope: phCalibration()?.slope_B ?? null,
                  mVOffset: phCalibration()?.offset_B ?? null,
                  phCalFactor: phCalibration()?.calFactor_B ?? null,
                  phCalOffset: phCalibration()?.calOffset_B ?? null,
                  rSquared: phCalibration()?.r2_B ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="C"
                [data]="{
                  kind: 'ph',
                  slope: phCalibration()?.slope_C ?? null,
                  mVOffset: phCalibration()?.offset_C ?? null,
                  phCalFactor: phCalibration()?.calFactor_C ?? null,
                  phCalOffset: phCalibration()?.calOffset_C ?? null,
                  rSquared: phCalibration()?.r2_C ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="D"
                [data]="{
                  kind: 'ph',
                  slope: phCalibration()?.slope_D ?? null,
                  mVOffset: phCalibration()?.offset_D ?? null,
                  phCalFactor: phCalibration()?.calFactor_D ?? null,
                  phCalOffset: phCalibration()?.calOffset_D ?? null,
                  rSquared: phCalibration()?.r2_D ?? null,
                }"
              ></app-flask-card>
            </div>
          }
          @if (
            calibrationType() === "sparging" && spargingCalibration()?.isFinal
          ) {
            <div class="flask-cards">
              <app-flask-card
                flaskLabel="A"
                [data]="{
                  kind: 'sparging',
                  slope: spargingCalibration()?.slope_A ?? null,
                  intercept: spargingCalibration()?.intercept_A ?? null,
                  rSquared: spargingCalibration()?.r2_A ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="B"
                [data]="{
                  kind: 'sparging',
                  slope: spargingCalibration()?.slope_B ?? null,
                  intercept: spargingCalibration()?.intercept_B ?? null,
                  rSquared: spargingCalibration()?.r2_B ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="C"
                [data]="{
                  kind: 'sparging',
                  slope: spargingCalibration()?.slope_C ?? null,
                  intercept: spargingCalibration()?.intercept_C ?? null,
                  rSquared: spargingCalibration()?.r2_C ?? null,
                }"
              ></app-flask-card>
              <app-flask-card
                flaskLabel="D"
                [data]="{
                  kind: 'sparging',
                  slope: spargingCalibration()?.slope_D ?? null,
                  intercept: spargingCalibration()?.intercept_D ?? null,
                  rSquared: spargingCalibration()?.r2_D ?? null,
                }"
              ></app-flask-card>
            </div>
          }
        </mat-expansion-panel>
      }
      @if (
        calibrationType() !== "optical-density" ||
        finalisedMessageSentToDevice() ||
        activeCalibrationIsFinal()
      ) {
        <mat-expansion-panel [expanded]="true" hideToggle>
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="expansion-header">Calibration Data</div>
            </mat-panel-title>
          </mat-expansion-panel-header>
          @if (calibrationType() === "optical-density") {
            @if (!odCalibration()?.isFinal && !odCalibration()?.isDeleted) {
              <div class="button--container no-padding">
                <app-icon-button
                  [label]="calibrationLabel()"
                  buttonId="poll-cal-btn"
                  (buttonClicked)="startCalibrationPolling()"
                  [isProcessing]="isCalibrationPolling()"
                  iconName="autorenew"
                  buttonShape="square"
                  buttonStyle="outlined"
                  [isDisabled]="isCalibrationPolling()"
                ></app-icon-button>
                <app-button
                  label="Stop Polling"
                  buttonId="stop-cal-polling-btn"
                  [isDisabled]="!isCalibrationPolling()"
                  (buttonClicked)="stopCalibrationPolling()"
                >
                </app-button>
              </div>
            }
            @if (odCalibration()?.isDeleted) {
              <app-status-indicator status="Deleted"></app-status-indicator>
            }
          }
          @if (calibrationType() === "oxygen") {
            @if (
              !oxygenCalibration()?.isFinal && !oxygenCalibration()?.isDeleted
            ) {
              <div id="oxygen-calibration-controls">
                <div id="notifications">
                  <app-slide-toggle
                    [parentForm]="oxygenNotificationsForm"
                    [field]="toggleOxygenNotifications"
                    tooltipText="Enable email notifications to be alerted when oxygen calibration
                is complete. Notifications are only available for calibrations
                that are in progress, and only one user can subscribe per
                calibration. If this toggle is disabled, another user has subscribed."
                  ></app-slide-toggle>
                </div>
                <div id="oxygen-button--container">
                  <app-icon-button
                    [label]="calibrationLabel()"
                    buttonId="poll-cal-btn"
                    (buttonClicked)="startOxygenPolling()"
                    [isProcessing]="isOxygenPolling()"
                    iconName="autorenew"
                    buttonShape="square"
                    buttonStyle="outlined"
                    [isDisabled]="
                      oxygenCalibration()?.isFinal || isOxygenPolling()
                    "
                  ></app-icon-button>
                  <app-button
                    label="Stop Polling"
                    buttonId="stop-cal-polling-btn"
                    [isDisabled]="!isOxygenPolling()"
                    (buttonClicked)="stopOxygenPolling()"
                  >
                  </app-button>
                </div>
              </div>
            }
            @if (oxygenCalibration()?.isDeleted) {
              <app-status-indicator status="Deleted"></app-status-indicator>
            }
          }
          @if (calibrationType() === "ph") {
            @if (!phCalibration()?.isFinal && !phCalibration()?.isDeleted) {
              <div class="button--container no-padding">
                <app-icon-button
                  [label]="calibrationLabel()"
                  buttonId="poll-cal-btn"
                  (buttonClicked)="startPHPolling()"
                  [isProcessing]="isPhPolling()"
                  iconName="autorenew"
                  buttonShape="square"
                  buttonStyle="outlined"
                  [isDisabled]="phCalibration()?.isFinal || isPhPolling()"
                ></app-icon-button>
                <app-button
                  label="Stop Polling"
                  buttonId="stop-cal-polling-btn"
                  [isDisabled]="!isPhPolling()"
                  (buttonClicked)="stopPHPolling()"
                >
                </app-button>
              </div>
            }
            @if (phCalibration()?.isDeleted) {
              <app-status-indicator status="Deleted"></app-status-indicator>
            }
          }
          @if (calibrationType() === "sparging") {
            @if (
              !spargingCalibration()?.isFinal &&
              !spargingCalibration()?.isDeleted
            ) {
              <div class="button--container no-padding">
                <app-icon-button
                  [label]="calibrationLabel()"
                  buttonId="poll-cal-btn"
                  (buttonClicked)="startSpargingPolling()"
                  [isProcessing]="isSpargingPolling()"
                  iconName="autorenew"
                  buttonShape="square"
                  buttonStyle="outlined"
                  [isDisabled]="
                    spargingCalibration()?.isFinal || isSpargingPolling()
                  "
                ></app-icon-button>
                <app-button
                  label="Stop Polling"
                  buttonId="stop-cal-polling-btn"
                  [isDisabled]="!isSpargingPolling()"
                  (buttonClicked)="stopSpargingPolling()"
                >
                </app-button>
              </div>
            }
            @if (spargingCalibration()?.isDeleted) {
              <app-status-indicator status="Deleted"></app-status-indicator>
            }
          }
          @if (calibrationTypeId() === CalibrationTypeEnum.BaseModule) {
            <div class="section--container">
              @if (dataError()?.message) {
                <div class="error">
                  Data refresh failed:
                  {{ dataError()?.message || "Please try again." }}
                </div>
              }
              @if (renderCalibrationGraph()) {
                <div class="page-refreshed subtitle1">
                  Page refreshed:
                  {{ timeOfLastUpdate() | date: "HH:mm a, dd/MM/yy" }}
                </div>
                <app-calibration-graph
                  [calibrationData]="calibrationData()"
                  [graphId]="'calibration-graph'"
                  [yAxisLabel]="'Voltage [V]'"
                  [results]="odCalibration()"
                >
                </app-calibration-graph>
              } @else {
                <div class="loading--container">
                  @if (dataIsProcessing()) {
                    <app-loading-spinner
                      [label]="calibrationLoadingText"
                    ></app-loading-spinner>
                  } @else if (
                    odCalibration()?.isFinal &&
                    calibrationData()?.flasks?.length === 0
                  ) {
                    <div class="no-data--container">
                      <mat-icon class="empty-state--icon" aria-hidden="true"
                        >bar_chart</mat-icon
                      >
                      <h6>No graph data</h6>
                      <div class="subtitle1">
                        Calibration has been edited or created offline.
                      </div>
                    </div>
                  } @else {
                    <div class="no-data--container">
                      <mat-icon class="empty-state--icon" aria-hidden="true"
                        >bar_chart</mat-icon
                      >
                      <h6>No data received</h6>
                    </div>
                  }
                </div>
              }
            </div>
          }
          @if (calibrationTypeId() === CalibrationTypeEnum.Oxygen) {
            <div class="section--container">
              <div class="selection--container">
                <app-chip-radio-button
                  [field]="saturationToggle"
                  [parentForm]="selectSaturationForm"
                  [currentValue]="
                    selectSaturationForm?.controls[saturationToggle.key]?.value
                  "
                  (radioButtonChange)="
                    handleOxygenSaturationToggle(
                      selectSaturationForm?.controls[saturationToggle.key]
                        ?.value
                    )
                  "
                ></app-chip-radio-button>
              </div>
              @if (dataError()?.message) {
                <div class="error">
                  Data refresh failed:
                  {{ dataError()?.message || "Please try again." }}
                </div>
              }
              @if (renderCalibrationGraph()) {
                <div class="subtitle1">
                  Page refreshed:
                  {{ timeOfLastUpdate() | date: "HH:mm a, dd/MM/yy" }}
                </div>
                <app-oxygen-calibration-graph
                  [calibrationData]="oxygenCalibrationData()"
                  [graphId]="'calibration-graph'"
                  [yAxisLabel]="'dphi'"
                ></app-oxygen-calibration-graph>
              } @else {
                <div class="loading--container">
                  @if (dataIsProcessing() || oxygenDataReloading()) {
                    <app-loading-spinner
                      [label]="calibrationLoadingText"
                    ></app-loading-spinner>
                  } @else if (
                    oxygenCalibration()?.isFinal &&
                    oxygenCalibrationData()?.flasks?.length === 0
                  ) {
                    <div class="no-data--container">
                      <mat-icon class="empty-state--icon" aria-hidden="true"
                        >bar_chart</mat-icon
                      >
                      <h6>No graph data</h6>
                      <div class="subtitle1">
                        Calibration has been edited or created offline.
                      </div>
                    </div>
                  } @else {
                    <div class="no-data--container">
                      <mat-icon class="empty-state--icon" aria-hidden="true"
                        >bar_chart</mat-icon
                      >
                      <h6>No data received</h6>
                    </div>
                  }
                </div>
              }
            </div>
          }
          @if (calibrationTypeId() === CalibrationTypeEnum.pH) {
            <div class="section--container">
              @if (dataError()?.message) {
                <div class="error">
                  Data refresh failed:
                  {{ dataError()?.message || "Please try again." }}
                </div>
              }
              @if (renderCalibrationGraph()) {
                <div class="subtitle1">
                  Page refreshed:
                  {{ timeOfLastUpdate() | date: "HH:mm a, dd/MM/yy" }}
                </div>
                <app-ph-calibration-graph
                  [calibrationData]="phCalibrationData()"
                  [graphId]="'calibration-graph'"
                  yAxisLabel="Voltage (mV)"
                >
                </app-ph-calibration-graph>
              } @else {
                <div class="loading--container">
                  @if (dataIsProcessing()) {
                    <app-loading-spinner
                      [label]="calibrationLoadingText"
                    ></app-loading-spinner>
                  } @else if (
                    phCalibration()?.isFinal &&
                    phCalibrationData()?.flasks?.length === 0
                  ) {
                    <div class="no-data--container">
                      <mat-icon class="empty-state--icon" aria-hidden="true"
                        >bar_chart</mat-icon
                      >
                      <h6>No graph data</h6>
                      <div class="subtitle1">
                        Calibration has been edited or created offline.
                      </div>
                    </div>
                  } @else {
                    <div class="no-data--container">
                      <mat-icon class="empty-state--icon" aria-hidden="true"
                        >bar_chart</mat-icon
                      >
                      <h6>No data received</h6>
                    </div>
                  }
                </div>
              }
            </div>
          }
          @if (calibrationTypeId() === CalibrationTypeEnum.Sparging) {
            <div class="section--container">
              @if (dataError()?.message) {
                <div class="error">
                  Data refresh failed:
                  {{ dataError()?.message || "Please try again." }}
                </div>
              }
              @if (renderCalibrationGraph()) {
                <div class="subtitle1">
                  Page refreshed:
                  {{ timeOfLastUpdate() | date: "HH:mm a, dd/MM/yy" }}
                </div>
                <app-sparging-calibration-graph
                  [calibrationData]="spargingCalibrationData()"
                  [graphId]="'calibration-graph'"
                  [yAxisLabel]="'Flow Rate [mL/min]'"
                >
                </app-sparging-calibration-graph>
              } @else {
                <div class="loading--container">
                  @if (dataIsProcessing()) {
                    <app-loading-spinner
                      [label]="calibrationLoadingText"
                    ></app-loading-spinner>
                  } @else if (
                    spargingCalibration()?.isFinal &&
                    spargingCalibrationData()?.flasks?.length === 0
                  ) {
                    <div class="no-data--container">
                      <mat-icon class="empty-state--icon" aria-hidden="true"
                        >bar_chart</mat-icon
                      >
                      <h6>No graph data</h6>
                      <div class="subtitle1">
                        Calibration has been edited or created offline.
                      </div>
                    </div>
                  } @else {
                    <div class="no-data--container">
                      <mat-icon class="empty-state--icon" aria-hidden="true"
                        >bar_chart</mat-icon
                      >
                      <h6>No data received</h6>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </mat-expansion-panel>
      }
      <mat-expansion-panel expanded hideToggle>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <div class="expansion-header">History</div>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <div id="history--container">
          <table mat-table [dataSource]="changelogList()">
            <ng-container matColumnDef="date">
              <th
                mat-header-cell
                *matHeaderCellDef
                (click)="onSort('CreatedAt')"
                (keydown.enter)="onSort('CreatedAt')"
                (keydown.space)="$event.preventDefault(); onSort('CreatedAt')"
                [class.active]="currentSort().sortBy === 'CreatedAt'"
                [attr.aria-sort]="
                  currentSort().sortBy === 'CreatedAt'
                    ? currentSort().sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                "
                tabindex="0"
                role="columnheader"
              >
                <span class="sortable">
                  Date
                  <span class="sort-icons" aria-hidden="true">
                    <mat-icon>arrow_drop_up</mat-icon>
                    <mat-icon>arrow_drop_down</mat-icon>
                  </span>
                </span>
              </th>
              <td mat-cell *matCellDef="let log">
                {{ log.createdAt | date: "dd/MM/y - h:mm a" }}
              </td>
            </ng-container>

            <ng-container matColumnDef="note">
              <th
                mat-header-cell
                *matHeaderCellDef
                (click)="onSort('NoteText')"
                (keydown.enter)="onSort('NoteText')"
                (keydown.space)="$event.preventDefault(); onSort('NoteText')"
                [class.active]="currentSort().sortBy === 'NoteText'"
                [attr.aria-sort]="
                  currentSort().sortBy === 'NoteText'
                    ? currentSort().sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                "
                tabindex="0"
                role="columnheader"
              >
                <span class="sortable">
                  Note
                  <span class="sort-icons" aria-hidden="true">
                    <mat-icon>arrow_drop_up</mat-icon>
                    <mat-icon>arrow_drop_down</mat-icon>
                  </span>
                </span>
              </th>
              <td mat-cell *matCellDef="let log">{{ log.noteTitle }}</td>
            </ng-container>

            <ng-container matColumnDef="change">
              <th
                mat-header-cell
                *matHeaderCellDef
                (click)="onSort('ChangeText')"
                (keydown.enter)="onSort('ChangeText')"
                (keydown.space)="$event.preventDefault(); onSort('ChangeText')"
                [class.active]="currentSort().sortBy === 'ChangeText'"
                [attr.aria-sort]="
                  currentSort().sortBy === 'ChangeText'
                    ? currentSort().sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                "
                tabindex="0"
                role="columnheader"
              >
                <span class="sortable">
                  Logged Change
                  <span class="sort-icons" aria-hidden="true">
                    <mat-icon>arrow_drop_up</mat-icon>
                    <mat-icon>arrow_drop_down</mat-icon>
                  </span>
                </span>
              </th>
              <td mat-cell *matCellDef="let log">{{ log.noteText }}</td>
            </ng-container>

            <ng-container matColumnDef="user">
              <th
                mat-header-cell
                *matHeaderCellDef
                (click)="onSort('CreatedBy')"
                (keydown.enter)="onSort('CreatedBy')"
                (keydown.space)="$event.preventDefault(); onSort('CreatedBy')"
                [class.active]="currentSort().sortBy === 'CreatedBy'"
                [attr.aria-sort]="
                  currentSort().sortBy === 'CreatedBy'
                    ? currentSort().sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                "
                tabindex="0"
                role="columnheader"
              >
                <span class="sortable">
                  User
                  <span class="sort-icons" aria-hidden="true">
                    <mat-icon>arrow_drop_up</mat-icon>
                    <mat-icon>arrow_drop_down</mat-icon>
                  </span>
                </span>
              </th>
              <td mat-cell *matCellDef="let log">{{ log.createdBy }}</td>
            </ng-container>

            <tr
              mat-header-row
              *matHeaderRowDef="changelogDisplayedColumns"
            ></tr>
            <tr
              mat-row
              *matRowDef="let row; columns: changelogDisplayedColumns"
            ></tr>
            <tr *matNoDataRow>
              <td [attr.colspan]="4" class="empty-state">
                <mat-icon class="empty-state--icon" aria-hidden="true"
                  >article</mat-icon
                >
                <p class="empty-state--title">
                  {{
                    changelogError()
                      ? changelogError().message
                      : "No history entries"
                  }}
                </p>
              </td>
            </tr>
          </table>
          <div id="pagination--container">
            <app-dropdown-form-field
              id="changelogs-per-page"
              [field]="changeLogsPerPageField"
              [parentForm]="paginationControls"
            ></app-dropdown-form-field>
            <div id="pagination--controls">
              <app-icon-button
                iconName="chevron_left"
                buttonId="previous"
                ariaLabel="Previous page"
                (buttonClicked)="getPrevious()"
                [isDisabled]="!hasPrevious()"
              ></app-icon-button>
              <span> {{ currentPage() }} / {{ totalPages() }} </span>
              <app-icon-button
                iconName="chevron_right"
                buttonId="next"
                ariaLabel="Next page"
                (buttonClicked)="getNext()"
                [isDisabled]="!hasNext()"
              ></app-icon-button>
            </div>
          </div>
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  </div>
}
<app-modal
  id="discard-success-response"
  titleText="Calibration deleted"
  primaryText="Choose an action below"
  buttonLayout="double-button"
  primaryButtonText="Go to dashboard"
  primaryButtonId="success-primary-button"
  secondaryButtonText="Create new calibration"
  secondaryButtonId="success-secondary-button"
  modalStyle="success"
  (modalButtonClicked)="modalButtonClicked($event)"
  [allowClose]="false"
>
</app-modal>
<app-modal
  id="notification-success"
  [titleText]="notificationModalTitle()"
  [primaryText]="notificationModalText()"
  primaryButtonText="Close"
  primaryButtonId="notifications-close-button"
  modalStyle="success"
  (modalButtonClicked)="modalButtonClicked($event)"
>
</app-modal>
<app-modal
  id="notification-error"
  titleText="Error"
  [primaryText]="notificationError()?.message"
  primaryButtonText="Close"
  primaryButtonId="notifications-error-button"
  modalStyle="error"
  (modalButtonClicked)="modalButtonClicked($event)"
>
</app-modal>
<app-modal
  id="calibration-polling-error"
  titleText="Error"
  [primaryText]="pollingError()?.message"
  primaryButtonText="Close"
  primaryButtonId="calibration-polling-error"
  modalStyle="error"
  (modalButtonClicked)="modalButtonClicked($event)"
>
</app-modal>
<app-modal
  id="voltage-error"
  titleText="Error"
  [primaryText]="voltageError()?.message"
  primaryButtonText="Close"
  primaryButtonId="voltage-error-button"
  modalStyle="error"
  (modalButtonClicked)="modalButtonClicked($event)"
>
</app-modal>
<app-modal
  id="finalisation-error"
  titleText="Error"
  [primaryText]="finalisationError()?.message"
  primaryButtonText="Close"
  primaryButtonId="finalisation-error-button"
  modalStyle="error"
  (modalButtonClicked)="modalButtonClicked($event)"
>
</app-modal>
<app-modal
  id="discard-error-response"
  titleText="Unable to delete calibration"
  [primaryText]="
    discardError()?.message
      ? discardError().message + ' Choose an action below.'
      : 'Choose an action below.'
  "
  buttonLayout="double-button"
  primaryButtonText="Go to dashboard"
  primaryButtonId="error-primary-button"
  secondaryButtonText="Close"
  secondaryButtonId="error-secondary-button"
  modalStyle="error"
  (modalButtonClicked)="modalButtonClicked($event)"
  [allowClose]="false"
>
</app-modal>
<app-validation-modal
  id="delete-calibration"
  [titleText]="validationTitleText()"
  validationText="delete"
  primaryButtonText="Confirm"
  primaryButtonId="confirm-delete-button"
  secondaryButtonText="Cancel"
  secondaryButtonId="cancel-delete-button"
  (modalButtonClicked)="discardCalibration()"
  [isProcessing]="discardIsProcessing()"
></app-validation-modal>
<app-modal
  id="attention"
  titleText="Attention"
  primaryText="Your changes have not been saved. Press cancel to return to the form and save your changes, or continue to discard."
  buttonLayout="double-button"
  primaryButtonText="Continue"
  primaryButtonId="confirm-navigation"
  secondaryButtonText="Cancel"
  secondaryButtonId="cancel-navigation"
  modalStyle="warning"
  [allowClose]="false"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
